import { Alert } from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Outlet,
  createFileRoute,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"

import { AttemptsService, QuizzesService } from "@/client"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { QuizInterface } from "@/components/TestTaking/QuizInterface"
import { setLastQuestionIndex } from "@/lib/quizAttemptStorage"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

export const Route = createFileRoute(
  "/_layout/quizzes/$quizId/attempt/$attemptId/question/$questionIndex",
)({
  component: QuizAttemptPage,
})

function QuizAttemptPage() {
  const { quizId, attemptId, questionIndex } = Route.useParams()
  const navigate = useNavigate()
  const { location } = useRouterState()
  const queryClient = useQueryClient()
  const [isQuizSubmitted, setIsQuizSubmitted] = useState(false)
  const submitInFlightRef = useRef(false)
  const requestedQuestionIndex = Number.parseInt(questionIndex, 10)
  const safeRequestedIndex = Number.isNaN(requestedQuestionIndex)
    ? 0
    : Math.max(requestedQuestionIndex, 0)

  const {
    data: quiz,
    isLoading: isLoadingQuiz,
    error: quizError,
  } = useQuery({
    queryKey: ["quiz", quizId],
    queryFn: () => QuizzesService.getQuiz({ quizId }),
    retry: false,
  })

  const { data: quizSections, isLoading: isLoadingSections } = useQuery({
    queryKey: ["quiz-sections", quizId],
    queryFn: () => QuizzesService.listQuizSections({ quizId }),
    enabled: Boolean(quiz),
    retry: false,
  })

  const {
    data: attemptData,
    isLoading: isLoadingAttempt,
    error: attemptError,
    isFetched: isAttemptFetched,
  } = useQuery({
    queryKey: ["quiz-current-attempt", quizId],
    queryFn: () => AttemptsService.getCurrentAttempt({ quizId }),
    enabled: Boolean(quiz) && !isQuizSubmitted,
    retry: false,
  })

  const [latestAttempt, setLatestAttempt] = useState<typeof attemptData | null>(
    null,
  )

  useEffect(() => {
    if (attemptData) {
      setLatestAttempt(attemptData)
    }
  }, [attemptData])

  const resolvedAttempt = attemptData ?? latestAttempt

  useEffect(() => {
    if (!isAttemptFetched || isQuizSubmitted) {
      return
    }
    // Redirect if we've fetched and confirmed there's no attempt
    // This includes both error cases and successful null responses (no active attempt)
    if (!resolvedAttempt) {
      navigate({
        to: "/quizzes/$quizId",
        params: { quizId },
        replace: true,
      })
    }
  }, [isAttemptFetched, resolvedAttempt, navigate, quizId, isQuizSubmitted])

  const activeAttemptId = resolvedAttempt?.attempt_id ?? attemptId

  useEffect(() => {
    if (!resolvedAttempt || resolvedAttempt.attempt_id === attemptId) {
      return
    }
    navigate({
      to: "/quizzes/$quizId/attempt/$attemptId/question/$questionIndex",
      params: {
        quizId,
        attemptId: resolvedAttempt.attempt_id,
        questionIndex,
      },
      replace: true,
    })
  }, [resolvedAttempt, attemptId, quizId, navigate, questionIndex])

  const totalQuestions = quiz?.questions?.length ?? 0
  const clampedQuestionIndex =
    totalQuestions > 0
      ? Math.min(Math.max(safeRequestedIndex, 0), totalQuestions - 1)
      : 0

  useEffect(() => {
    if (!quiz || totalQuestions === 0) {
      return
    }
    if (clampedQuestionIndex !== safeRequestedIndex) {
      navigate({
        to: "/quizzes/$quizId/attempt/$attemptId/question/$questionIndex",
        params: {
          quizId,
          attemptId: activeAttemptId,
          questionIndex: clampedQuestionIndex.toString(),
        },
        replace: true,
      })
    }
  }, [
    quiz,
    totalQuestions,
    clampedQuestionIndex,
    safeRequestedIndex,
    navigate,
    quizId,
    activeAttemptId,
  ])

  useEffect(() => {
    if (!activeAttemptId) {
      return
    }
    setLastQuestionIndex(activeAttemptId, clampedQuestionIndex)
  }, [activeAttemptId, clampedQuestionIndex])

  const submitQuizMutation = useMutation({
    mutationFn: (id: string) =>
      AttemptsService.submitQuiz({
        quizId,
        requestBody: { attempt_id: id },
      }),
    onSuccess: (data) => {
      setIsQuizSubmitted(true)

      // Invalidate student dashboard and attempt history
      queryClient.invalidateQueries({
        queryKey: ["dashboard", "student"],
      })
      queryClient.invalidateQueries({
        queryKey: ["my-attempts"],
      })

      // Redirect to review page immediately if results are available
      if (data.can_review && data.attempt_id) {
        navigate({
          to: "/quizzes/$quizId/attempts/$attemptId/review",
          params: {
            quizId,
            attemptId: data.attempt_id,
          },
          replace: true,
        })
      } else {
        // If results are not available, invalidate queries and stay on results page
        queryClient.invalidateQueries({
          queryKey: ["quiz-current-attempt", quizId],
        })
      }
    },
  })

  const submitAnswerMutation = useMutation({
    mutationFn: (payload: {
      attemptId: string
      questionId: string
      selectedAnswer: number
      timeSpent: number
    }) =>
      AttemptsService.submitAnswer({
        quizId,
        requestBody: {
          attempt_id: payload.attemptId,
          question_id: payload.questionId,
          selected_answer: payload.selectedAnswer,
          time_spent_seconds: payload.timeSpent,
        },
      }),
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        ["quiz-current-attempt", quizId],
        (previous: typeof attemptData) => {
          if (!previous) return previous
          const filtered = previous.answers.filter(
            (answer) => answer.question_id !== variables.questionId,
          )
          return {
            ...previous,
            answers: [
              ...filtered,
              {
                question_id: variables.questionId,
                selected_answer: variables.selectedAnswer,
                time_spent_seconds: variables.timeSpent,
              },
            ],
          }
        },
      )
    },
  })

  const breadcrumbs = useRouteBreadcrumbs("quizzes.index", [
    { label: quiz?.name ?? "Quiz", href: `/quizzes/${quizId}` },
    { label: "Attempt", isCurrent: true },
  ])

  const initialAnswers = useMemo(() => {
    if (!resolvedAttempt) {
      return {}
    }
    return resolvedAttempt.answers.reduce<Record<string, number>>(
      (acc, answer) => {
        acc[answer.question_id] = answer.selected_answer
        return acc
      },
      {},
    )
  }, [resolvedAttempt])

  const quizForInterface = useMemo(() => {
    if (!quiz) return null
    const sectionMap = new Map(
      (quizSections ?? quiz.sections ?? []).map((section) => [
        section.id,
        section,
      ]),
    )
    return {
      id: quiz.id,
      name: quiz.name,
      questions: (quiz.questions || []).map((q) => ({
        id: q.question_id,
        question_text: q.question_text,
        options: q.options,
        rationale: q.rationale,
        section: (() => {
          if (!q.section_id) return null
          const section = sectionMap.get(q.section_id)
          if (!section) return null
          return { id: section.id, name: section.name }
        })(),
      })),
    }
  }, [quiz, quizSections])

  if (isLoadingQuiz || isLoadingSections) {
    return (
      <PageShell
        title="Quiz Attempt"
        breadcrumbs={breadcrumbs}
        description="Loading quiz content..."
      >
        <PageSkeleton variant="detail" rows={4} />
      </PageShell>
    )
  }

  if (quizError) {
    return (
      <PageShell
        title="Quiz Attempt"
        breadcrumbs={breadcrumbs}
        description="Unable to load quiz."
      >
        <Alert.Root status="error">
          <Alert.Title>Unable to load quiz</Alert.Title>
          <Alert.Description>
            {(quizError as Error).message || "Please try again later."}
          </Alert.Description>
        </Alert.Root>
      </PageShell>
    )
  }

  if (attemptError) {
    return (
      <PageShell
        title="Quiz Attempt"
        breadcrumbs={breadcrumbs}
        description="Unable to load attempt."
      >
        <Alert.Root status="error">
          <Alert.Title>Unable to load attempt</Alert.Title>
          <Alert.Description>
            {(attemptError as Error).message || "Please try again later."}
          </Alert.Description>
        </Alert.Root>
      </PageShell>
    )
  }

  if (!quizForInterface || !activeAttemptId) {
    return null
  }

  const handleAnswerSelect = async (
    questionId: string,
    answerIndex: number,
    timeSpent: number,
  ): Promise<void> => {
    if (!activeAttemptId) return
    await submitAnswerMutation.mutateAsync({
      attemptId: activeAttemptId,
      questionId,
      selectedAnswer: answerIndex,
      timeSpent,
    })
  }

  const handleSubmit = () => {
    if (!activeAttemptId) return
    // Idempotency guard: ignore submits while one is already in flight or has
    // succeeded. Prevents a runaway client (held Enter key, render loop) from
    // firing the submit endpoint in a burst and prematurely finalizing the
    // attempt. Reset on settle so a failed submit can be retried.
    if (submitInFlightRef.current || submitQuizMutation.isSuccess) return
    submitInFlightRef.current = true
    submitQuizMutation.mutate(activeAttemptId, {
      onSettled: () => {
        submitInFlightRef.current = false
      },
    })
  }

  const handleNavigateToReview = (params: {
    questionIndex: number
    questionId: string
  }) => {
    if (!activeAttemptId) {
      return
    }
    navigate({
      to: "/quizzes/$quizId/attempt/$attemptId/question/$questionIndex/review",
      params: {
        quizId,
        attemptId: activeAttemptId,
        questionIndex: params.questionIndex.toString(),
      },
      search: {
        questionId: params.questionId,
        ...(totalQuestions
          ? { totalQuestions: totalQuestions.toString() }
          : {}),
      },
    })
  }

  const handleQuestionChange = (nextIndex: number) => {
    if (!activeAttemptId) return
    const normalizedIndex =
      totalQuestions > 0
        ? Math.min(Math.max(nextIndex, 0), totalQuestions - 1)
        : 0
    navigate({
      to: "/quizzes/$quizId/attempt/$attemptId/question/$questionIndex",
      params: {
        quizId,
        attemptId: activeAttemptId,
        questionIndex: normalizedIndex.toString(),
      },
    })
  }

  const initialTimeRemaining = resolvedAttempt?.time_remaining_seconds ?? null

  if (location.pathname.endsWith("/review")) {
    return <Outlet />
  }

  return (
    <PageShell
      title={quiz?.name ?? "Quiz Attempt"}
      breadcrumbs={breadcrumbs}
      fullWidth
      contentGap={4}
    >
      <QuizInterface
        quiz={quizForInterface}
        attemptId={activeAttemptId}
        timeLimit={quiz?.time_limit_minutes}
        initialAnswers={initialAnswers}
        initialTimeRemaining={initialTimeRemaining}
        initialQuestionIndex={clampedQuestionIndex}
        onAnswerSelect={handleAnswerSelect}
        onSubmit={handleSubmit}
        isSubmitting={submitQuizMutation.isPending}
        isSubmitted={submitQuizMutation.isSuccess}
        results={submitQuizMutation.data}
        isLoading={isLoadingAttempt && !resolvedAttempt}
        error={(attemptError as unknown as Error) || null}
        onQuestionChange={handleQuestionChange}
        showRationaleMode={quiz?.show_rationale_mode}
        onNavigateToReview={handleNavigateToReview}
        enableStudentTimer={quiz?.allow_student_timer === true}
      />
    </PageShell>
  )
}
